/**
 * Sidebar navigation model — single source of truth.
 *
 * P-S616-1: this list used to live inside Layout, so nothing outside the sidebar could
 * answer "which page may this role actually open?". Post-login redirect and the
 * access-denied escape hatch now resolve their target from this same ordered list, so a
 * role can never be sent to a page its own sidebar would not show it.
 */

import React from 'react';
import {
  RiDashboardLine,
  RiBuildingLine,
  RiFileListLine,
  RiFileList2Line,
  RiUserSettingsLine,
  RiSettingsLine,
  RiBug2Line,
  RiTeamLine,
  RiBarcodeBoxLine,
  RiFileChartLine,
  RiStoreLine,
  RiCustomerServiceLine,
  RiDeviceLine,
  RiFileUploadLine,
  RiFileDownloadLine,
  RiAlertLine,
  RiTimeLine,
  RiFileTextLine,
  RiTruckLine,
  RiToolsLine,
  RiServerLine,
  RiSignalTowerLine,
  RiMailSettingsLine,
  RiUserStarLine,
  RiCodeLine,
  RiArrowGoBackLine,
  RiMoneyDollarCircleLine,
  RiCalendarCheckLine,
  RiSimCard2Line,
  RiNotificationLine,
  RiArchiveLine,
  RiStackLine,
  RiInboxUnarchiveLine,
  RiInboxArchiveLine,
  RiExchangeBoxLine,
  RiEditBoxLine,
  RiClipboardLine,
} from 'react-icons/ri';

// นิยามรายการเมนู
export interface MenuItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  permissions?: string[];
  submenu?: MenuItem[];
}

// รายการเมนูหลัก - ใช้ permission format: module.action
export const menuItems: MenuItem[] = [
    {
      title: 'แดชบอร์ด',
      path: '/dashboard',
      icon: <RiDashboardLine className="w-5 h-5" />,
      permissions: ['dashboard.view'],
    },
    {
      title: 'ซ่อมบำรุง',
      path: '/maintenance',
      icon: <RiToolsLine className="w-5 h-5" />,
      // R72 completeness: parent perms = union of all children so the group shows if a user
      // can reach ≥1 child (e.g. Support with only supplier_repairs.view must see this group).
      // accessory_requests.view ถูกถอดออกจาก union แล้ว — เมนู "ศูนย์ขออุปกรณ์เสริม" ถูกยุบ
      // เข้ากลุ่ม "คลังอุปกรณ์" (PO 2026-09-07) role ที่มีแค่สิทธิ์นั้นจึงไม่ควรเห็นกลุ่มซ่อมบำรุงเปล่า ๆ
      permissions: [
        'maintenance.view', 'maintenance.create', 'supplier_repairs.view',
        'equipment_returns.view', 'device_replacements.view',
        'device_movements.view',
        'maintenance_types.view', 'repair_methods.view',
      ],
      submenu: [
        {
          // ศูนย์เปลี่ยนอุปกรณ์ถูกยุบเข้ามาเป็นแท็บในหน้านี้ (PO 2026-09-07) → role ที่มีแค่
          // device_replacements.view ต้องยังเข้าถึงเมนูนี้ได้ (หน้าเปิดเฉพาะแท็บการเปลี่ยนอุปกรณ์ให้)
          title: 'รายการซ่อมบำรุง',
          path: '/maintenance/list',
          icon: <RiToolsLine className="w-5 h-5" />,
          permissions: ['maintenance.view', 'device_replacements.view'],
        },
        {
          title: 'บันทึกการซ่อมบำรุง',
          path: '/maintenance/create',
          icon: <RiToolsLine className="w-5 h-5" />,
          permissions: ['maintenance.create'],
        },
        {
          title: 'ติดตามการส่งซ่อม',
          path: '/supplier-repairs',
          icon: <RiTruckLine className="w-5 h-5" />,
          permissions: ['supplier_repairs.view'],
        },
        {
          title: 'ติดตามการคืนอุปกรณ์',
          path: '/rentals/equipment-returns',
          icon: <RiArrowGoBackLine className="w-5 h-5" />,
          permissions: ['equipment_returns.view'],
        },
        {
          title: 'ย้ายอุปกรณ์',
          path: '/rentals/device-movements',
          icon: <RiTruckLine className="w-5 h-5" />,
          permissions: ['device_movements.view'],
        },
        {
          title: 'ประเภทการซ่อมบำรุง',
          path: '/settings/maintenance-types',
          icon: <RiToolsLine className="w-5 h-5" />,
          permissions: ['maintenance_types.view'],
        },
        {
          title: 'วิธีการแก้ไข',
          path: '/settings/repair-methods',
          icon: <RiToolsLine className="w-5 h-5" />,
          permissions: ['repair_methods.view'],
        },
      ],
    },
    {
      title: 'ข้อมูลหลัก',
      path: '/master-data',
      icon: <RiBuildingLine className="w-5 h-5" />,
      // แสดง group ถ้าผู้ใช้ดู master-data entity ใดได้อย่างน้อยหนึ่ง (hasMenuPermission ใช้ .some)
      // เดิม parent นี้ขาด permissions → group โผล่กับทุก role แม้ child ถูก filter หมด → คลิกแล้วเด้ง /access-denied
      permissions: [
        'companies.view', 'sites.view', 'products.view', 'brands.view',
        'device_models.view', 'device_types.view', 'sims.view', 'devices.view',
        'authorized_signers.view', 'suppliers.view',
      ],
      submenu: [
        {
          title: 'ข้อมูลบริษัท',
          path: '/master-data/companies',
          icon: <RiBuildingLine className="w-5 h-5" />,
          permissions: ['companies.view'],
        },
        {
          title: 'ข้อมูลโครงการ',
          path: '/master-data/sites',
          icon: <RiStoreLine className="w-5 h-5" />,
          permissions: ['sites.view'],
        },
        {
          title: 'ข้อมูลสินค้า',
          path: '/master-data/products',
          icon: <RiBarcodeBoxLine className="w-5 h-5" />,
          permissions: ['products.view'],
        },
        {
          title: 'ข้อมูลยี่ห้อ',
          path: '/master-data/brands',
          icon: <RiBarcodeBoxLine className="w-5 h-5" />,
          permissions: ['brands.view'],
        },
        {
          title: 'รุ่นอุปกรณ์',
          path: '/master-data/device-models',
          icon: <RiDeviceLine className="w-5 h-5" />,
          permissions: ['device_models.view'],
        },
        {
          title: 'ประเภทอุปกรณ์',
          path: '/master-data/device-types',
          icon: <RiDeviceLine className="w-5 h-5" />,
          permissions: ['device_types.view'],
        },
        {
          title: 'ข้อมูล SIM',
          path: '/master-data/sims',
          icon: <RiSignalTowerLine className="w-5 h-5" />,
          permissions: ['sims.view'],
        },
        {
          title: 'แพ็กเกจซิม',
          path: '/master-data/sim-packages',
          icon: <RiSimCard2Line className="w-5 h-5" />,
          permissions: ['sims.view'],
        },
        {
          title: 'ข้อมูลอุปกรณ์',
          path: '/master-data/devices',
          icon: <RiServerLine className="w-5 h-5" />,
          permissions: ['devices.view'],
        },
        {
          title: 'ข้อมูลผู้ลงนาม',
          path: '/master-data/authorized-signers',
          icon: <RiUserStarLine className="w-5 h-5" />,
          permissions: ['authorized_signers.view'],
        },
        {
          title: 'ข้อมูลผู้จำหน่าย',
          path: '/master-data/suppliers',
          icon: <RiCustomerServiceLine className="w-5 h-5" />,
          permissions: ['suppliers.view'],
        },
      ],
    },
    {
      // Phase 2-3 (PO 2026-09-07): กลุ่มใหม่ ไม่ยัดใต้ "ซ่อมบำรุง" ที่มี 8 เมนูแล้ว
      // งานคลัง (เบิก/คืน/รับเข้า/ปรับยอด/สมุดบัญชี) เป็นคนละงานกับงานซ่อม
      title: 'คลังอุปกรณ์',
      path: '/stock',
      icon: <RiArchiveLine className="w-5 h-5" />,
      // parent = union ของ child (pattern เดียวกับกลุ่มอื่น) ไม่งั้น role ที่มีแค่
      // stock_movements.view มองไม่เห็นกลุ่มทั้งที่เข้า /stock/movements ได้
      permissions: ['stock_issues.view', 'stock_movements.view', 'stock_movements.adjust'],
      submenu: [
        {
          // FB-2026-0020 (มติ PO 3 พ.ย. 2026): ภาพรวมคงเหลือต่อรุ่น — คีย์ดูสต็อกเดิม ไม่สร้างคีย์ใหม่
          title: 'สรุปคงเหลือ',
          path: '/stock/summary',
          icon: <RiStackLine className="w-5 h-5" />,
          permissions: ['stock_movements.view'],
        },
        {
          title: 'ใบเบิก-ยืมอุปกรณ์',
          path: '/stock/issues',
          icon: <RiInboxUnarchiveLine className="w-5 h-5" />,
          permissions: ['stock_issues.view'],
        },
        {
          title: 'ของค้างคืน',
          path: '/stock/pending-returns',
          icon: <RiTimeLine className="w-5 h-5" />,
          permissions: ['stock_issues.view'],
        },
        {
          title: 'รับเข้าคลัง',
          path: '/stock/receive',
          icon: <RiInboxArchiveLine className="w-5 h-5" />,
          permissions: ['stock_movements.adjust'],
        },
        {
          title: 'ปรับปรุงยอด',
          path: '/stock/adjust',
          icon: <RiEditBoxLine className="w-5 h-5" />,
          permissions: ['stock_movements.adjust'],
        },
        {
          title: 'ความเคลื่อนไหวสต็อก',
          path: '/stock/movements',
          icon: <RiExchangeBoxLine className="w-5 h-5" />,
          permissions: ['stock_movements.view'],
        },
      ],
    },
    {
      title: 'การเช่า',
      path: '/rentals',
      icon: <RiTruckLine className="w-5 h-5" />,
      // P-S034-1: parent ต้องเป็น union ของ child permissions — ไม่งั้น role ที่มีแค่
      // rental_extensions.view มองไม่เห็นเมนู "คำขอต่ออายุรออนุมัติ" ทั้งที่เข้า route ได้
      permissions: [
        'rentals.view', 'rentals.create', 'rental_extensions.view',
        'preparation_jobs.view', 'preparation_jobs.create', 'quotations.view',
      ],
      submenu: [
        {
          title: 'รายการเช่า',
          path: '/rentals/list',
          icon: <RiFileListLine className="w-5 h-5" />,
          permissions: ['rentals.view'],
        },
        {
          // มติ PO 29 ก.ย. 2026 เฟส 4: ใบเสนอราคาคือต้นทางของดีล (เสนอ → สั่งเตรียมเครื่อง → สัญญา) — อยู่กลุ่ม "การเช่า"
          // FB-2026-0005 (3 พ.ย. 2026 · ผู้ใช้เคาะ): เดิมมีซ้ำในกลุ่ม "การเงิน" ด้วย (เมนูซ้ำ 2 ที่ + ไฮไลต์ 2 กลุ่ม)
          // → เหลือที่นี่ที่เดียว · role ที่มีแค่ quotations.view เห็นเมนูผ่านกลุ่มนี้ (permissions ของกลุ่มมี quotations.view)
          title: 'ใบเสนอราคา',
          path: '/billing/quotations',
          icon: <RiFileTextLine className="w-5 h-5" />,
          permissions: ['quotations.view'],
        },
        {
          // ใบงานเตรียมเครื่อง (PO 2026-09-08) — อยู่ใต้ "การเช่า" ไม่ใช่ "คลังอุปกรณ์"
          // เพราะเป็นขั้นตอนก่อนเกิดสัญญาเช่า ไม่ใช่งานคลังของอีกทีม
          // เซลมีแค่ .create (ไม่มี .view) ต้องเห็นเมนูนี้เพื่อกดเปิดใบ → permissions เป็น OR
          title: 'ใบงานเตรียมเครื่อง',
          path: '/rentals/preparation-jobs',
          icon: <RiClipboardLine className="w-5 h-5" />,
          permissions: ['preparation_jobs.view', 'preparation_jobs.create'],
        },
        {
          title: 'สร้างรายการเช่าใหม่',
          path: '/rentals/create',
          icon: <RiFileListLine className="w-5 h-5" />,
          permissions: ['rentals.create'],
        },
        {
          title: 'ติดตามการยกเลิก',
          path: '/rentals/cancellation-tracking',
          icon: <RiAlertLine className="w-5 h-5" />,
          permissions: ['rentals.view'],
        },
        {
          // S122-EXTQUEUE (PO 2026-08-19): เปิดคิวอนุมัติต่ออายุที่เดิมมีแต่ API
          title: 'คำขอต่ออายุรออนุมัติ',
          path: '/rentals/extension-approvals',
          icon: <RiTimeLine className="w-5 h-5" />,
          permissions: ['rental_extensions.view'],
        },
      ],
    },
    {
      title: 'สัญญาเช่า',
      path: '/contracts',
      icon: <RiFileTextLine className="w-5 h-5" />,
      permissions: ['contracts.view', 'contracts.create'],
      submenu: [
        {
          title: 'รายการสัญญาเช่า',
          path: '/contracts/list',
          icon: <RiFileListLine className="w-5 h-5" />,
          permissions: ['contracts.view'],
        },
        {
          title: 'สร้างสัญญาเช่าใหม่',
          path: '/contracts/create',
          icon: <RiFileListLine className="w-5 h-5" />,
          permissions: ['contracts.create'],
        },
        {
          title: 'ติดตามสถานะเอกสาร',
          path: '/contracts/tracking',
          icon: <RiFileListLine className="w-5 h-5" />,
          permissions: ['contracts.view'],
        },
      ],
    },
    {
      title: 'เอกสาร',
      path: '/documents',
      icon: <RiFileTextLine className="w-5 h-5" />,
      permissions: ['documents.view', 'documents.create'],
      submenu: [
        {
          title: 'รายการเอกสาร',
          path: '/documents/list',
          icon: <RiFileTextLine className="w-5 h-5" />,
          permissions: ['documents.view'],
        },
        {
          title: 'อัพโหลดเอกสาร',
          path: '/documents/upload',
          icon: <RiFileUploadLine className="w-5 h-5" />,
          permissions: ['documents.create'],
        },
      ],
    },
    {
      title: 'การเงิน',
      path: '/billing',
      icon: <RiMoneyDollarCircleLine className="w-5 h-5" />,
      // FB-2026-0005: ใบเสนอราคาย้ายไปอยู่กลุ่ม "การเช่า" ที่เดียว — ถอด quotations.view ออกจากกลุ่มนี้ด้วย
      // (ไม่งั้น role ที่มีแค่ quotations.view จะเห็นกลุ่ม "การเงิน" ว่างเปล่า)
      permissions: [
        'invoices.view', 'batch_invoices.view',
        'payments.view', 'tax_invoices.view', 'credit_notes.view',
      ],
      submenu: [
        {
          title: 'ใบแจ้งหนี้',
          path: '/billing/invoices',
          icon: <RiFileTextLine className="w-5 h-5" />,
          permissions: ['invoices.view'],
        },
        {
          title: 'สร้างใบแจ้งหนี้รายเดือน',
          path: '/billing/batch-invoices',
          icon: <RiCalendarCheckLine className="w-5 h-5" />,
          permissions: ['batch_invoices.view'],
        },
        {
          title: 'การชำระเงิน',
          path: '/billing/payments',
          icon: <RiMoneyDollarCircleLine className="w-5 h-5" />,
          permissions: ['payments.view'],
        },
        {
          title: 'ใบกำกับภาษี',
          path: '/billing/tax-invoices',
          icon: <RiFileTextLine className="w-5 h-5" />,
          permissions: ['tax_invoices.view'],
        },
        {
          title: 'ใบลดหนี้',
          path: '/billing/credit-notes',
          icon: <RiArrowGoBackLine className="w-5 h-5" />,
          permissions: ['credit_notes.view'],
        },
      ],
    },
    {
      title: 'รายงาน',
      path: '/reports',
      icon: <RiFileChartLine className="w-5 h-5" />,
      permissions: ['reports.view'],
      submenu: [
        {
          title: 'รายงานการเช่า',
          path: '/reports/rentals',
          icon: <RiFileChartLine className="w-5 h-5" />,
          permissions: ['reports.view'],
        },
        {
          title: 'รายงานสถานะอุปกรณ์',
          path: '/reports/devices',
          icon: <RiServerLine className="w-5 h-5" />,
          permissions: ['reports.view'],
        },
        {
          title: 'Dashboard ผู้บริหาร',
          path: '/reports/dashboard',
          icon: <RiDashboardLine className="w-5 h-5" />,
          permissions: ['reports.view'],
        },
        {
          title: 'รายการที่ใกล้หมดอายุ',
          path: '/reports/expiring',
          icon: <RiAlertLine className="w-5 h-5" />,
          permissions: ['reports.view'],
        },
        {
          title: 'รายงานการเงิน',
          path: '/reports/financial',
          icon: <RiMoneyDollarCircleLine className="w-5 h-5" />,
          permissions: ['reports.view'],
        },
        {
          title: 'รายงานสัญญา',
          path: '/reports/contracts',
          icon: <RiFileTextLine className="w-5 h-5" />,
          permissions: ['reports.view'],
        },
      ],
    },
    {
      title: 'การแจ้งเตือน',
      path: '/notifications',
      icon: <RiNotificationLine className="w-5 h-5" />,
      permissions: ['notifications.view'],
    },
    {
      title: 'ผู้ใช้งาน',
      path: '/users',
      icon: <RiTeamLine className="w-5 h-5" />,
      permissions: ['users.view', 'roles.view'],
      submenu: [
        {
          title: 'ข้อมูลผู้ใช้งาน',
          path: '/users/list',
          icon: <RiUserSettingsLine className="w-5 h-5" />,
          permissions: ['users.view'],
        },
        {
          title: 'การจัดการสิทธิ์',
          path: '/users/roles',
          icon: <RiUserSettingsLine className="w-5 h-5" />,
          permissions: ['roles.view'],
        },
      ],
    },
    {
      title: 'นำเข้า/ส่งออกข้อมูล',
      path: '/import',
      icon: <RiFileUploadLine className="w-5 h-5" />,
      // parent = union ของ child ทั้งสอง (imports.view + exports.view)
      // เดิม (G-08) นำเข้า derive จาก 10 key create/edit — เปลี่ยนเป็นหมวดแยก imports.view
      // เพื่อให้ปิด/เปิดจาก Role settings UI ตรงๆ ได้ (migration เติมให้ role เดิมแล้ว)
      permissions: ['imports.view', 'exports.view'],
      submenu: [
        {
          title: 'นำเข้าจาก Excel (ครบวงจร)',
          path: '/import/excel-bulk',
          icon: <RiFileTextLine className="w-5 h-5" />,
          // สิทธิ์รวมหมวด "การนำเข้า" (ตรงกับ route guard ใน App.tsx + backend base gate)
          permissions: ['imports.view'],
        },
        {
          title: 'ส่งออกข้อมูล',
          path: '/import/export',
          icon: <RiFileDownloadLine className="w-5 h-5" />,
          permissions: ['exports.view'],
        },
      ],
    },
    {
      title: 'ตั้งค่า',
      path: '/settings',
      icon: <RiSettingsLine className="w-5 h-5" />,
      // G-04 (ต่อจาก fix a75921f): parent = union ของ child ทั้งหมด — เพิ่ม notifications.view
      // ให้ role ที่มีแค่สิทธิ์นั้นเห็น child "ตั้งค่าการแจ้งเตือน" (pattern เดียวกับ "ซ่อมบำรุง")
      permissions: ['settings.view', 'settings.edit', 'notifications.view', 'feedback.view'],
      submenu: [
        // === หมวด: ระบบ ===
        // G-05: "ตั้งค่าทั่วไป" (settings.view) ต้องเป็น submenu[0] เสมอ — parentMenuTarget
        // ใน Layout เช็ค child ตัวแรก ถ้าเข้าไม่ได้จะ fallback ไป child อื่นแทน /settings hub
        // ทำให้ role ที่มี settings.view แต่ไม่มี settings.edit โดนเด้งไป /settings/notifications
        {
          title: 'ตั้งค่าทั่วไป',
          path: '/settings/general',
          icon: <RiSettingsLine className="w-5 h-5" />,
          permissions: ['settings.view'],
        },
        // === หมวด: องค์กร ===
        {
          title: 'ข้อมูลองค์กร',
          path: '/settings/organization',
          icon: <RiBuildingLine className="w-5 h-5" />,
          // G-22: route gate ด้วย settings.view (อ่านได้ ปุ่มบันทึกค่อย gate ด้วย .edit ในหน้า)
          permissions: ['settings.view'],
        },
        // ซ่อนไว้ก่อน — auto-sync config ยังไม่ persist ลง DB
        // {
        //   title: 'เชื่อมต่อ Flow Account',
        //   path: '/settings/flow-account',
        //   icon: <RiCloudLine className="w-5 h-5" />,
        //   permissions: ['sync.flowaccount'],
        // },
        // === หมวด: เอกสาร ===
        {
          title: 'ตั้งค่าเอกสาร PDF',
          path: '/settings/pdf-settings',
          icon: <RiFileTextLine className="w-5 h-5" />,
          permissions: ['settings.view'], // G-22
        },
        {
          title: 'ตั้งค่าใบกำกับภาษี',
          path: '/settings/tax-invoice',
          icon: <RiFileList2Line className="w-5 h-5" />,
          permissions: ['settings.view'], // G-22
        },
        {
          title: 'ตั้งค่ารหัสอัตโนมัติ',
          path: '/settings/code-templates',
          icon: <RiCodeLine className="w-5 h-5" />,
          permissions: ['settings.edit'],
        },
        // === หมวด: ติดตามปัญหา ===
        {
          title: 'แจ้งปัญหา / Feedback',
          path: '/feedback',
          icon: <RiBug2Line className="w-5 h-5" />,
          permissions: ['feedback.view'],
        },
        // === หมวด: การแจ้งเตือน ===
        {
          title: 'ตั้งค่าการแจ้งเตือน',
          path: '/settings/notifications',
          icon: <RiMailSettingsLine className="w-5 h-5" />,
          permissions: ['notifications.view'],
        },
      ],
    },
  ];

/**
 * เส้นทางแรกที่ role หนึ่งเปิดได้จริง ไล่ตามลำดับเมนูใน sidebar
 *
 * P-S616-1: เดิม post-login redirect ตายตัวไป `/dashboard` → role ที่ไม่มี `dashboard.view`
 * เจอ "ไม่มีสิทธิ์เข้าถึง" ทันทีหลัง login. resolver นี้เดินเมนูชุดเดียวกับที่ sidebar ใช้
 * render จึงรับประกันว่าปลายทางเป็นเมนูที่ผู้ใช้มองเห็นและกดได้จริงเสมอ
 *
 * @param canAccess - callback ตรวจสิทธิ์ (ควร return true เสมอสำหรับ super admin)
 * @returns path ที่เข้าถึงได้ตัวแรก · ถ้าไม่มีเลยคืน `/dashboard` ให้ ProtectedRoute
 *          พาไปหน้า access-denied ตามปกติ (ซึ่งตอนนี้มี sidebar ให้หนีต่อได้)
 */
export const resolveLandingRoute = (
  canAccess: (permissions?: string[]) => boolean
): string => {
  for (const item of menuItems) {
    // parent ที่มี submenu: path ของตัวเองอาจ redirect ไป child ตัวแรกที่อาจไม่มีสิทธิ์
    // → เลือก child ที่เข้าถึงได้จริงเสมอ (ตรรกะเดียวกับ parentMenuTarget ใน Layout)
    if (item.submenu && item.submenu.length > 0) {
      const child = item.submenu.find(sub => canAccess(sub.permissions));
      if (child) return child.path;
      continue;
    }
    if (canAccess(item.permissions)) return item.path;
  }
  return '/dashboard';
};
